//@ts-check
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import React, { useEffect, useState } from 'react'
import Actions, { addEpic } from 'Flux/index.js'
import { ofType } from 'redux-observable'
import { filter, ignoreElements, merge, mergeMap, of, tap } from 'rxjs'
import { useDispatch } from 'react-redux'
import { AjaxError } from 'rxjs/ajax'
import type { Action, PayloadAction } from '@reduxjs/toolkit'
import { addMessage, removeMessage } from '../../../Flux/Slice/Application/ApplicationSlice.js'
import { Status, errorMessage, infoMessage, successMessage } from '../../../Model/Message/IMessage.js'
import { useGetAuthenticatedUser, useGetMessages } from '../../../Flux/Selector/index.js'
import { RequestInProgressWidget } from '../RequestInProgressWidget.js'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { TypedErrorTypess } from '../../../Generated/TypedErrorTypes.js'
import { ErrorWidget } from '../../Layout/PageLayout/ErrorWidget.js'

const severityStyles = {
  [Status.SUCCESS]: 'bg-green-50 text-green-900 border-green-400',
  [Status.ERROR]: 'bg-red-50 text-red-900 border-red-400',
  [Status.INFO]: 'bg-blue-50 text-blue-900 border-blue-400'
}

function isErrorPayloadAction(a: Action): a is PayloadAction<{ error: Error }> {
  return 'payload' in a && typeof a.payload === 'object' && a.payload !== null && 'error' in a.payload && a.payload.error instanceof Error
}

function Alert(props: { severity: Status; onClose?: () => void; children: ReactNode; className?: string }) {
  return (
    // <div role="alert" className={`z-50 alert alert-${props.severity} ${props.className || ''}`}>
    <div
      role="alert"
      className={`
        flex items-center gap-3 px-4 py-2 
        rounded-lg border shadow-sm
        transition-all duration-200 ease-in-out
        ${severityStyles[props.severity]}
        ${props.className || ''}
      `}
    >
      {props.severity === Status.INFO && (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current h-6 w-6 shrink-0">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          ></path>
        </svg>
      )}
      {props.severity === Status.SUCCESS && (
        <CheckCircleIcon className="z-50 h-6 w-6 stroke-green-400" />
        // <svg xmlns="http://www.w3.org/2000/svg" className="z-50 h-6 w-6 shrink-0 stroke-current" fill="none" viewBox="0 0 24 24">
        //   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        // </svg>
      )}
      {props.severity === Status.ERROR && (
        <svg xmlns="http://www.w3.org/2000/svg" className="z-50 h-6 w-6 shrink-0 stroke-current" fill="none" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      )}
      {props.children}
    </div>
  )
}

const ServerResponseContext = React.createContext({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  success: (_message: unknown) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  error: (_message: unknown) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  info: (_message: unknown) => {}
})


interface ServerResponseProviderProps {
  portal?: unknown
  children?: ReactNode
}

function ServerResponseProvider(props: ServerResponseProviderProps) {
  const messages = useGetMessages()
  const dispatch = useDispatch()
  const [showUnauthorized, setShowUnauthorized] = useState<boolean>(false)
  const [unauthorizedMessage, setShowUnauthorizedMessage] = useState<string | undefined>(undefined)
  const authenticatedUser = useGetAuthenticatedUser()

  useEffect(() => {
    const deref = addEpic((action$) => {
      return merge(
        action$.pipe(
          ofType(Actions.authentication.authenticationExpired.type),
          tap((action: PayloadAction<{ failedRequest: string; message?: string; showAlways?: boolean }>) => {
            if (!window.location.search.includes('token=') || action.payload.showAlways) {
              console.warn('Authentication Expired! Relogging required!', action.payload)
              setShowUnauthorizedMessage(action.payload.message)
              setShowUnauthorized(true)
            }
          }),
          ignoreElements()
        ),
        action$.pipe(
          filter((a) => window.location.pathname != '/' && a.type.endsWith('ailure')),
          tap((e) => {
            if (!isErrorPayloadAction(e)) {
              console.warn('received a non-error payload Action in the Failure handler!', e)
            }
          }),
          filter(isErrorPayloadAction),
          mergeMap((e: PayloadAction<{ error: Error }>) => {
            const error = e.payload.error
            if (
              error instanceof AjaxError &&
              error.response != null &&
              typeof error.response === 'object' &&
              Object.hasOwn(error.response, 'type') &&
              error.response.type === TypedErrorTypess.TYPED_ERROR.key
            ) {
              return of(addMessage(errorMessage(`Operation ${e.type} failed:\n${error.response.message} (code ${error.status})`)))
            } else {
              return of(addMessage(errorMessage(e.type + ': ' + e.payload?.error?.message + ' ' + (e.payload?.error as AjaxError)?.status)))
            }
          })
        ),
        action$.pipe(
          filter((a) => a.type.endsWith('uccess')),
          mergeMap((e: Action) => {
            return of(addMessage(successMessage(e.type)))
          })
        )
      )
    })

    return () => deref()
  }, [])

  const successMessages = useMemo(() => messages.filter((x) => x.type === Status.SUCCESS), [messages])

  const newestSuccessMessage = useMemo(
    () => (successMessages.length > 0 ? successMessages[successMessages.length - 1] : undefined),
    [successMessages]
  )

  const infoMessages = useMemo(() => messages.filter((x) => x.type === Status.INFO), [messages])

  const newestInfoMessage = useMemo(() => (infoMessages.length > 0 ? infoMessages[infoMessages.length - 1] : undefined), [infoMessages])

  const [expandMessages, setExpandMessages] = useState<boolean>(false)

  return (
    <ServerResponseContext.Provider
      value={{
        success: (msg) => dispatch(addMessage(successMessage('' + msg))),
        info: (msg) => dispatch(addMessage(infoMessage('' + msg))),
        error: (msg) => dispatch(addMessage(errorMessage('' + msg)))
      }}
      {...props}
    >
      <>
        <RequestInProgressWidget />
        <div
          id={'tour-errors'}
          className="print:hidden fixed top-16 left-1/2 max-h-64 z-50 overflow-y-auto w-full max-w-3xl"
          style={{ transform: 'translateX(calc(-50% + 40px))' }}
        >
          {showUnauthorized && (
            <ErrorWidget
              error={
                <span>
                  {unauthorizedMessage ?? 'User Authentication Expired! Please'}{' '}
                  <a
                    href="/"
                    className="text-gray-400 font-bold"
                    onClick={(e) => {
                      e.preventDefault() // prevent default browser redirect
                      localStorage.setItem('postLoginRedirect', location.pathname + location.search)
                      window.location.href = '/' // trigger redirect manually after storage
                    }}
                  >
                    relogin
                  </a>
                  !
                </span>
              }
              onRemove={() => setShowUnauthorized(false)}
            />
          )}
          {messages
            .filter((x) => x.type === Status.ERROR)
            .map((e, index) => (
              <ErrorWidget key={`${e}-${index}`} error={e.message} onRemove={() => dispatch(removeMessage(e.id))} />
            ))}
          {/* Info messages are deliberate, user-facing feedback ("22 files were skipped", "please
              provide a non-empty order name") — but they used to render only in the admin-only corner
              below, so for a normal customer they were dispatched into the void. They live here, next
              to errors, and auto-dismiss via MessageEpic. Successes stay admin-only: those are the
              firehose of every *Success action type, not something a customer should read. */}
          {newestInfoMessage && (
            <div className="m-2" onClick={() => dispatch(removeMessage(newestInfoMessage.id))}>
              <Alert severity={Status.INFO}>
                <span className="flex flex-col">
                  {newestInfoMessage.message.split('\n').map((line, index) => (
                    <span key={index}>{line}</span>
                  ))}
                </span>
                {infoMessages.length > 1 && <span className="text-xs text-gray-500">({infoMessages.length} messages)</span>}
              </Alert>
            </div>
          )}
        </div>
        {props.children}
        {authenticatedUser?.showAdminUx && (
          <div
            id="tour-actions"
            className="print:hidden d-print-none fixed bottom-1 right-1 gap-y-1 flex flex-col mr-2 mb-1 min-w-96 z-50"
            style={{ maxWidth: 650 }}
          >
            {expandMessages ? (
              <>
                {messages
                  .filter((x) => x.type === Status.SUCCESS)
                  .map((st) => (
                    <div onClick={() => setExpandMessages(false)} key={st.id}>
                      <Alert severity={st.type}>{st.message}</Alert>
                    </div>
                  ))}
              </>
            ) : (
              <>
                {newestSuccessMessage && (
                  <div onClick={() => setExpandMessages(true)} key={newestSuccessMessage.id}>
                    <Alert severity={newestSuccessMessage.type}>
                      {newestSuccessMessage.message}{' '}
                      {successMessages.length > 1 && <span className="text-xs text-gray-500">({successMessages.length} messages)</span>}
                    </Alert>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </>
    </ServerResponseContext.Provider>
  )
}

const useServerFeedback = () => React.useContext(ServerResponseContext)

export { ServerResponseProvider, useServerFeedback }
